import React, { useState } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../../../styles/theme';
import type { GroupMemberRole } from '../groupsSlice';

/**
 * Member row in `GroupDetailScreen`. Renders both ACTIVE members + still-
 * INVITED pending invitees, with role-aware affordances on each:
 *
 *   ACTIVE member (caller = OWNER/ADMIN, target != OWNER):
 *     [⋯ menu] → Make admin · Make owner · Remove
 *
 *   INVITED row (caller = OWNER/ADMIN):
 *     [Resend]  [Cancel]
 *
 *   ACTIVE member viewing themselves:
 *     no actions; "you" appended to name
 *
 *   ACTIVE member with role=OWNER:
 *     no menu unless caller is also OWNER (transfer flow); even then
 *     the only option is to demote self via Make owner on someone else
 *     (atomic swap is handled by the BE per SD-7)
 *
 * FE-216 (cancel invite) + FE-217 (role-change menu) merged in one
 * pass since they share this component.
 */

type MemberRole = GroupMemberRole;

/**
 * Gap #110 (2026-05-27 audit) — render redacted form of an email for
 * the GroupDetail members list. OWNER/ADMIN see this (need it to
 * verify who they invited); MEMBER role doesn't see emails at all
 * (handled at the call site). Pattern matches Slack / 1Password /
 * Apple ID redactions:
 *   "kanishk+e2e-marcus@remembr.xyz" → "k••@remembr.xyz"
 *
 * Single-letter prefix + two bullet glyphs keeps the line short while
 * still letting the owner recognize the address they invited (combined
 * with the display name above it). The full email is one tap-and-hold
 * away via the row action menu (deferred — not needed for v1 audit).
 */
function redactEmail(email: string): string {
  if (!email || !email.includes('@')) return email;
  const [local, domain] = email.split('@');
  if (!local || !domain) return email;
  const head = local[0] ?? '?';
  return `${head}••@${domain}`;
}

interface MemberRowProps {
  name?: string;
  email: string;
  role?: MemberRole;
  avatarInitial?: string;
  /** "Pending" invitation rows render with a mail icon instead of
      initials, and show an "Invited Xd ago" badge + Cancel / Resend. */
  pending?: boolean;
  pendingSince?: string;
  /** ACTIVE member's user_id when known — required for role-change
      ops. Pending rows leave this null. */
  userId?: number | null;
  /** Is this row the current user (self)? Suppresses the menu. */
  isSelf?: boolean;
  /** Is the caller OWNER of the group? Drives access to the "Make
      owner" / "Demote owner" menu entries. */
  callerIsOwner?: boolean;
  /** Is the caller ADMIN (or OWNER)? Drives visibility of any actions
      on other rows. Non-admin non-OWNER members never see actions. */
  callerIsAdmin?: boolean;

  // ── Action callbacks (all optional — parent only wires what it wants)
  /** Existing — fires DELETE /groups/{id}/members/{user_id}. */
  onRemove?: () => void;
  /** FE-216 — fires DELETE /groups/{id}/members/by-email/{email}. */
  onCancelInvite?: () => void;
  /** FE-216 — fires POST /groups/{id}/members again with the same
      email so BE rotates the token + re-sends the email. Server returns
      201 on success (no conflict — same group_member row updates). */
  onResendInvite?: () => void;
  /** FE-217 — fires PATCH /groups/{id}/members/{user_id} with the new
      role. Atomic OWNER swap (per SD-7) is server-side. */
  onChangeRole?: (next: GroupMemberRole) => void;
}

export const MemberRow: React.FC<MemberRowProps> = ({
  name,
  email,
  role,
  avatarInitial,
  pending = false,
  pendingSince,
  userId,
  isSelf = false,
  callerIsOwner = false,
  callerIsAdmin = false,
  onRemove,
  onCancelInvite,
  onResendInvite,
  onChangeRole,
}) => {
  const initial =
    avatarInitial ||
    ((name || email).split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() ||
      '?');

  // FE-217 — menu open state + per-action loading spinner. Inline
  // state because the menu is row-local (no global concern) and the
  // loading flag covers the brief network round-trip.
  const [menuOpen, setMenuOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<
    null | 'remove' | 'admin' | 'owner' | 'member' | 'cancel' | 'resend'
  >(null);

  // Decide which menu entries to render. The matrix from § D.1:
  //   target=MEMBER, caller=OWNER  → Make admin · Make owner · Remove
  //   target=MEMBER, caller=ADMIN  → Remove (no role change without OWNER)
  //   target=ADMIN,  caller=OWNER  → Demote to member · Make owner · Remove
  //   target=ADMIN,  caller=ADMIN  → (no actions on a peer admin — BE
  //                                   enforces, but FE hides too)
  //   target=OWNER,  caller=*      → no menu (last-owner-demote 400 is
  //                                   surfaced via Make-owner-on-OTHER)
  //   isSelf                       → no menu
  const showMenu =
    !pending &&
    !isSelf &&
    role !== 'OWNER' &&
    callerIsAdmin &&
    (role === 'MEMBER' ? true : callerIsOwner) &&
    !!onChangeRole;
  // Remove is still allowed even when role transitions aren't (e.g.,
  // ADMIN removing a peer MEMBER without changing roles).
  const showRemoveInMenu = !pending && !isSelf && callerIsAdmin && !!onRemove;

  const doAction = async (
    kind: 'remove' | 'admin' | 'owner' | 'member',
  ) => {
    setMenuOpen(false);
    setPendingAction(kind);
    try {
      if (kind === 'remove') onRemove?.();
      else if (kind === 'admin') onChangeRole?.('ADMIN');
      else if (kind === 'owner') onChangeRole?.('OWNER');
      else if (kind === 'member') onChangeRole?.('MEMBER');
    } finally {
      // Optimistic — parent slice mutates store, so we just clear the
      // local pending state on next tick. The button will already be
      // gone (or re-rendered with the new role) by the time this fires.
      setTimeout(() => setPendingAction(null), 800);
    }
  };

  const doCancelInvite = () => {
    setPendingAction('cancel');
    onCancelInvite?.();
    setTimeout(() => setPendingAction(null), 800);
  };
  const doResendInvite = () => {
    setPendingAction('resend');
    onResendInvite?.();
    setTimeout(() => setPendingAction(null), 800);
  };

  return (
    <View style={[styles.row, pending && styles.rowPending]}>
      <View style={[styles.avatar, pending && styles.avatarPending]}>
        {pending ? (
          <MaterialIcons name="mail" size={18} color={fpColors.outline} />
        ) : (
          <Text style={styles.avatarText}>{initial}</Text>
        )}
      </View>
      <View style={styles.body}>
        <Text style={styles.name} numberOfLines={1}>
          {name || email}
          {isSelf ? ' (you)' : null}
        </Text>
        {/* Gap #110 (2026-05-27 audit) — render rules for the second
            line, by viewer role:

              caller is OWNER / ADMIN ........ show redacted email
                                               ("k••@remembr.xyz")
              caller is MEMBER ............... hide email entirely
                                               (privacy: members don't
                                               need each other's emails;
                                               Founsi mediates contact)
              row is the viewer themselves ... show full email
                                               (it's their own account)

            Pending-invite rows always show the email since the OWNER
            is the only person who sees them (per render gate above)
            and they need to verify who they're sending to. */}
        {!!name &&
          (isSelf ? (
            <Text style={styles.email} numberOfLines={1}>
              {email}
            </Text>
          ) : callerIsAdmin ? (
            <Text style={styles.email} numberOfLines={1}>
              {redactEmail(email)}
            </Text>
          ) : null)}
        {pending && pendingSince && (
          <Text style={styles.pendingMeta}>Invited {pendingSince}</Text>
        )}
      </View>

      <View style={styles.right}>
        {pending ? (
          <>
            <View style={styles.pendingBadge}>
              <Text style={styles.pendingBadgeText}>Invited</Text>
            </View>
            {callerIsAdmin && onResendInvite ? (
              <Pressable
                onPress={doResendInvite}
                disabled={pendingAction === 'resend'}
                style={({ pressed }) => [
                  styles.smallBtn,
                  pressed && { opacity: 0.85 },
                  pendingAction === 'resend' && { opacity: 0.55 },
                ]}
                accessibilityRole="button"
                accessibilityLabel="Resend invite"
                testID={`member-row-resend-${email}`}
              >
                <Text style={styles.smallBtnText}>Resend</Text>
              </Pressable>
            ) : null}
            {callerIsAdmin && onCancelInvite ? (
              <Pressable
                onPress={doCancelInvite}
                disabled={pendingAction === 'cancel'}
                style={({ pressed }) => [
                  styles.smallBtn,
                  styles.smallBtnDanger,
                  pressed && { opacity: 0.85 },
                  pendingAction === 'cancel' && { opacity: 0.55 },
                ]}
                accessibilityRole="button"
                accessibilityLabel={`Cancel invite to ${email}`}
                testID={`member-row-cancel-invite-${email}`}
              >
                <Text
                  style={[styles.smallBtnText, { color: fpColors.error }]}
                >
                  Cancel
                </Text>
              </Pressable>
            ) : null}
          </>
        ) : (
          <>
            {role === 'OWNER' || role === 'ADMIN' ? (
              <View style={styles.adminBadge}>
                <Text style={styles.adminBadgeText}>
                  {role === 'OWNER' ? 'Owner' : 'Admin'}
                </Text>
              </View>
            ) : null}

            {showMenu || showRemoveInMenu ? (
              <Pressable
                onPress={() => setMenuOpen((v) => !v)}
                hitSlop={6}
                style={styles.menuBtn}
                accessibilityRole="button"
                accessibilityLabel={
                  menuOpen ? 'Close member menu' : 'Open member menu'
                }
                testID={`member-row-menu-${userId ?? email}`}
              >
                <MaterialIcons
                  name="more-vert"
                  size={20}
                  color={fpColors.onSurfaceVariant}
                />
              </Pressable>
            ) : (
              onRemove &&
              callerIsAdmin &&
              !isSelf &&
              role !== 'OWNER' && (
                <Pressable
                  onPress={onRemove}
                  hitSlop={6}
                  style={styles.removeBtn}
                  accessibilityLabel={`Remove ${name ?? email}`}
                >
                  <MaterialIcons
                    name="close"
                    size={18}
                    color={fpColors.outline}
                  />
                </Pressable>
              )
            )}
          </>
        )}
      </View>

      {/* FE-217 — Dropdown menu. Rendered inline below the row so we
          don't need a floating-menu portal. Closes on any action. */}
      {menuOpen && !pending && (
        <View style={styles.menu} testID={`member-row-menu-list-${userId}`}>
          {role === 'MEMBER' && callerIsAdmin ? (
            <MenuItem
              icon="admin-panel-settings"
              label="Make admin"
              onPress={() => doAction('admin')}
              loading={pendingAction === 'admin'}
            />
          ) : null}
          {role === 'ADMIN' && callerIsOwner ? (
            <MenuItem
              icon="person"
              label="Demote to member"
              onPress={() => doAction('member')}
              loading={pendingAction === 'member'}
            />
          ) : null}
          {callerIsOwner && role !== 'OWNER' ? (
            <MenuItem
              icon="star"
              label="Make owner"
              hint="You'll become an admin"
              onPress={() => doAction('owner')}
              loading={pendingAction === 'owner'}
            />
          ) : null}
          {showRemoveInMenu ? (
            <MenuItem
              icon="person-remove"
              label="Remove from circle"
              destructive
              onPress={() => doAction('remove')}
              loading={pendingAction === 'remove'}
            />
          ) : null}
        </View>
      )}
    </View>
  );
};

const MenuItem: React.FC<{
  icon: keyof typeof MaterialIcons.glyphMap;
  label: string;
  hint?: string;
  destructive?: boolean;
  loading?: boolean;
  onPress: () => void;
}> = ({ icon, label, hint, destructive, loading, onPress }) => (
  <Pressable
    onPress={onPress}
    disabled={loading}
    style={({ pressed }) => [
      styles.menuItem,
      pressed && { backgroundColor: fpColors.surfaceContainer },
      loading && { opacity: 0.55 },
    ]}
    accessibilityRole="menuitem"
    accessibilityLabel={label}
  >
    <MaterialIcons
      name={icon}
      size={18}
      color={destructive ? fpColors.error : fpColors.onSurfaceVariant}
    />
    <View style={{ flex: 1 }}>
      <Text
        style={[
          styles.menuItemLabel,
          destructive && { color: fpColors.error },
        ]}
      >
        {label}
      </Text>
      {hint ? <Text style={styles.menuItemHint}>{hint}</Text> : null}
    </View>
    {loading ? <ActivityIndicator color={fpColors.primary} size="small" /> : null}
  </Pressable>
);

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    borderRadius: 12,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    // Allow the inline menu to overflow downward via flexWrap.
    flexWrap: 'wrap',
  },
  rowPending: {
    borderStyle: 'dashed',
    backgroundColor: fpColors.surfaceContainerLow,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: fpColors.primaryContainer,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarPending: {
    backgroundColor: fpColors.surfaceContainer,
  },
  avatarText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.onPrimaryContainer,
  },
  body: { flex: 1, minWidth: 0 },
  name: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
  },
  email: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    marginTop: 1,
  },
  pendingMeta: {
    fontFamily: 'Inter_500Medium',
    fontSize: 11,
    color: fpColors.outline,
    marginTop: 2,
  },
  right: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  adminBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
    backgroundColor: `${fpColors.primary}1a`,
  },
  adminBadgeText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 10,
    color: fpColors.primary,
    letterSpacing: 0.3,
  },
  pendingBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
    backgroundColor: fpColors.tertiaryContainer,
  },
  pendingBadgeText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 10,
    color: fpColors.onTertiaryContainer,
    letterSpacing: 0.3,
  },
  menuBtn: {
    width: 28,
    height: 28,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: fpColors.surfaceContainerLow,
  },
  removeBtn: {
    width: 28,
    height: 28,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
  },
  smallBtn: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    backgroundColor: fpColors.surface,
  },
  smallBtnDanger: {
    borderColor: `${fpColors.error}55`,
  },
  smallBtnText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 11,
    color: fpColors.onSurface,
  },
  // FE-217 — inline dropdown menu. Spans the full row width below the
  // body so it doesn't need portal positioning logic.
  menu: {
    width: '100%',
    marginTop: 8,
    borderRadius: 10,
    backgroundColor: fpColors.surfaceContainerLow,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    overflow: 'hidden',
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  menuItemLabel: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 13,
    color: fpColors.onSurface,
  },
  menuItemHint: {
    fontFamily: 'Inter_400Regular',
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
    marginTop: 1,
  },
});
